/**
 * A barra de navegação do template.
 *
 * Diferente do cabeçalho do resto do produto: aqui ela é uma pílula flutuante
 * que paira sobre a hero (`position: absolute; top: 2rem`), com os links num
 * bloco de vidro no centro e o CTA solto à direita. É desenho de cartaz, e
 * funciona porque a hero atrás dela tem uma tela inteira de altura.
 *
 * Por isso ela vive só na home. Nas outras páginas continua valendo o
 * `SiteHeader`, que é fixo e ganha fundo ao rolar — uma pílula flutuante sobre
 * uma tabela de classificação não teria o que flutuar por cima.
 *
 * O menu de "Páginas" do template é um dropdown com três colunas de links
 * institucionais (404, style guide, changelog, licenças). Aqui ele lista o que
 * a MPC tem de verdade: regulamento, estatísticas e suporte. Manter as colunas
 * do template com os nomes do template seria copiar o esqueleto junto com o
 * conteúdo de exemplo.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { BotaoTema } from '@/components/BotaoTema';
import { MenuCelular } from '@/components/MenuCelular';
import { SessionArea } from '@/components/SiteHeader';
import { NomeDaMarca } from '@/components/LogoMPC';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';

const LINKS = [
  { to: '/', rotulo: 'Início', end: true },
  { to: '/campeonatos', rotulo: 'Campeonatos' },
  { to: '/times', rotulo: 'Times' },
] as const;

const PAGINAS = [
  [
    { to: '/regulamento', rotulo: 'Regulamento' },
    { to: '/estatisticas', rotulo: 'Estatísticas' },
  ],
  [
    { to: '/suporte', rotulo: 'Suporte' },
    { to: '/entrar', rotulo: 'Área do capitão' },
  ],
] as const;

export function NavbarTemplate() {
  const [aberto, setAberto] = useState(false);
  const [paginasAbertas, setPaginasAbertas] = useState(false);
  const fechar = useCallback(() => setAberto(false), []);
  const { isStaff, user } = useAuth();
  const local = useLocation();

  // Trocar de rota fecha o menu. Sem isto, quem toca num link no celular vai
  // para a página nova com o painel do menu ainda por cima dela.
  useEffect(() => {
    setAberto(false);
    setPaginasAbertas(false);
  }, [local.pathname]);

  return (
    <div role="banner" className="navbar">
      <div className="navbar-wrapper">
        {/*
          O emblema entra como `<img class="nav-logo">` cru, e não pelo
          componente `LogoMPC`.

          O componente trava altura e largura em pixels — é o que se quer no
          resto do produto, onde a marca tem um tamanho fixo. Aqui quem manda é
          o template: `.navbar-brand` dá o teto de 8.625rem e `.nav-logo` estica
          até ele, e esse par muda sozinho nos três breakpoints. Passar medida em
          pixels por cima seria desligar justamente a parte responsiva.
        */}
        <Link to="/" className="navbar-brand" aria-label="MPC — página inicial">
          <img src="/logo.webp" alt="" aria-hidden draggable={false} className="nav-logo" />
          <NomeDaMarca />
        </Link>

        <nav
          className={cn('nav-menu-wrapper', aberto && 'aberto')}
          aria-label="Principal"
          id="menu-principal"
        >
          <div className="nav-menu-items">
            {LINKS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={'end' in item ? item.end : false}
                className={({ isActive }) => cn('nav-link', isActive && 'w--current')}
              >
                {item.rotulo}
              </NavLink>
            ))}

            <div
              className="nav-dropdown"
              onMouseEnter={() => setPaginasAbertas(true)}
              onMouseLeave={() => setPaginasAbertas(false)}
            >
              <button
                type="button"
                className="nav-dropdown-toggle"
                aria-expanded={paginasAbertas}
                onClick={() => setPaginasAbertas((v) => !v)}
              >
                <div>Páginas</div>
                <svg viewBox="0 0 16 16" fill="none" className="dropdown-arrow" aria-hidden>
                  <path
                    d="M12.5625 4.6875L8.23438 9.01562L3.89062 4.6875L2.45312 6.125L8.21875 12.1875L14 6.125L12.5625 4.6875Z"
                    fill="currentColor"
                  />
                </svg>
              </button>

              {paginasAbertas && (
                <nav className="nav-dropdown-list w--open" aria-label="Páginas">
                  <div className="nav-dropdown-collection">
                    {PAGINAS.map((coluna, i) => (
                      <div key={i} className="nav-dropdown-items">
                        {coluna.map((item) => (
                          <Link key={item.to} to={item.to} className="nav-dropdown-link">
                            {item.rotulo}
                          </Link>
                        ))}
                      </div>
                    ))}
                  </div>
                </nav>
              )}
            </div>

            {/* A central de comando entra na navegação para quem é da
                organização: quem administra a competição não deveria caçar o
                painel dentro de um menu de avatar. */}
            {isStaff && (
              <NavLink to="/admin" end className="nav-link">
                Painel
              </NavLink>
            )}

          </div>
        </nav>

        <div className="nav-buttons desk">
          <BotaoTema className="mr-3" />
          {/* Padrão de acesso: quem já tem conta entra, quem não tem cria. */}
          {!user && (
            <Link to="/entrar" className="transparent-button mr-3">
              <div className="transparent-button-text">Entrar</div>
              <div className="transparent-button-bg" aria-hidden />
            </Link>
          )}
          {/*
            Logado, o canto direito mostra quem é — moedas, notificações e o
            nome com o menu da conta —, como nas outras páginas. Antes a home
            mostrava só "Ver campeonatos", e não havia como saber se a sessão
            estava aberta. `mpc-sempre-escuro`: no tema claro, o menu da conta
            precisa das cores escuras para ler sobre a hero.
          */}
          {user ? (
            <div className="mpc-sempre-escuro">
              <SessionArea />
            </div>
          ) : (
            <BotaoPrincipal logado={false} />
          )}
        </div>

        <button
          type="button"
          className="menu-button"
          aria-expanded={aberto}
          aria-controls="menu-celular"
          aria-label={aberto ? 'Fechar menu' : 'Abrir menu'}
          onClick={() => setAberto((v) => !v)}
        >
          {/*
            Três traços que viram X. O template usa uma animação Lottie de 40 KB
            para isto; dois `transform` fazem o mesmo gesto sem download nenhum.
          */}
          <span aria-hidden className="menu-icone">
            <span className={cn('menu-traco', aberto && 'aberto')} />
            <span className={cn('menu-traco', aberto && 'aberto')} />
            <span className={cn('menu-traco', aberto && 'aberto')} />
          </span>
        </button>
      </div>

      {/*
        No celular, o painel de vidro do template dá lugar ao menu de tela
        cheia do produto: o dropdown de páginas em colunas vazava pelas bordas
        e o vidro deixava a hero aparecer através dos links.
      */}
      <MenuCelular aberto={aberto} onFechar={fechar} />
    </div>
  );
}

function BotaoPrincipal({ logado }: { logado: boolean }) {
  return (
    <Link to={logado ? '/campeonatos' : '/cadastrar-time'} className="primary-button">
      <div className="primary-button-text">{logado ? 'Ver campeonatos' : 'Criar conta'}</div>
      <div className="primary-bg" aria-hidden />
    </Link>
  );
}
